import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Card,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import {
  useCreateDoorCode,
  useCreateDoorCodeSeries,
  useDeleteDoorCodeSeries,
  useDoorCodes,
  useDoorCodeSeries,
  useDoors,
  useLocationTree,
  useProviderCapabilities,
  useRollDoorCodeSeries,
  useSchedules,
  useSystems,
  useUpdateDoorCode,
  useUpdateDoorCodeSeries,
} from '../api/hooks'
import { utcDate } from '../lib/format'
import { locationPaths } from '../lib/locations'
import { readOnlyReason } from '../lib/dryrun'
import type {
  Door,
  DoorCode,
  DoorCodeSeries,
  DoorCodeSeriesCadence,
  LocationNode,
  RollSummary,
} from '../api/types'
import { ScopePicker } from '../components/features/ScopePicker'
import { SelectField } from './Directory'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** Door-code validity is a calendar date stored at 00:00Z (the rotation engine
 *  and this page's date inputs both write it so): shown -- and round-tripped
 *  into the `<input type="date">` values -- as that UTC date, see `utcDate`. */
function fmtDate(iso: string | null): string | null {
  return iso ? utcDate(iso) : null
}

/** ACTIVE / EXPIRING / EXPIRED computed from valid_to (mirrors Locations). */
function doorCodeStatus(c: DoorCode): { tone: 'ok' | 'warn' | 'muted'; label: string } {
  if (c.status !== 'active') return { tone: 'muted', label: c.status }
  if (c.valid_to) {
    const until = new Date(c.valid_to).getTime()
    if (!Number.isNaN(until)) {
      if (until < Date.now()) return { tone: 'muted', label: 'Expired' }
      if (until < Date.now() + 14 * 86400 * 1000) return { tone: 'warn', label: 'Expiring' }
    }
  }
  return { tone: 'ok', label: 'Active' }
}

/** Roll summaries carry uid LISTS for created/retired/pruned (counts typed
 *  defensively) — always render the count. */
function countOf(v: string[] | number): number {
  return Array.isArray(v) ? v.length : v
}

function rollSummaryLine(s: RollSummary): string {
  const parts = [
    `${countOf(s.created)} created`,
    `${countOf(s.retired)} retired`,
    `${countOf(s.pruned)} pruned`,
    `${s.attached} attached`,
    `${s.bound} bound`,
  ]
  return s.errors.length > 0 ? `${parts.join(' · ')} — ${s.errors.length} error(s)` : parts.join(' · ')
}

/** JM-style display end: the TRUE period end — valid_to minus the series'
 *  effective overlap (JM shows the 1st of the next month even though
 *  validity extends past it). */
function displayEndDate(validTo: string | null, overlapDays: number): string {
  if (!validTo) return 'open'
  const d = new Date(validTo)
  if (Number.isNaN(d.getTime())) return validTo
  // UTC arithmetic: a local setDate() across a DST change moves 00:00Z by an
  // hour and can land on the previous UTC day.
  d.setUTCDate(d.getUTCDate() - overlapDays)
  return utcDate(d.toISOString())
}

/** The uid set of a location and every descendant (inclusive). */
function subtreeLocationUids(nodes: LocationNode[], uid: string): Set<string> {
  const out = new Set<string>()
  const collect = (n: LocationNode) => {
    out.add(n.unique_id)
    n.children.forEach(collect)
  }
  const find = (ns: LocationNode[]): LocationNode | null => {
    for (const n of ns) {
      if (n.unique_id === uid) return n
      const hit = find(n.children)
      if (hit) return hit
    }
    return null
  }
  const node = find(nodes)
  if (node) collect(node)
  return out
}

/** The newsletter lines' name side: every door in the series scope
 *  (explicit doors + doors at-or-under each scope location) as
 *  "breadcrumb / Door name", plus any scope location with NO doors yet
 *  (its path stands in for the door). Sorted by name. */
function seriesScopeLines(
  series: DoorCodeSeries,
  doors: Door[],
  tree: LocationNode[],
  pathLabels: Map<string, string>,
): string[] {
  const names: string[] = []
  const direct = new Set(series.doors)
  const subtreeByLocation = series.locations.map((uid) => subtreeLocationUids(tree, uid))
  const coveredLocations = new Set<string>()
  for (const d of doors) {
    const viaIdx = subtreeByLocation.findIndex((sub) => sub.has(d.location))
    if (!direct.has(d.unique_id) && viaIdx < 0) continue
    names.push([...d.breadcrumb, d.name].join(' / '))
    if (viaIdx >= 0) coveredLocations.add(series.locations[viaIdx])
  }
  for (const uid of series.locations) {
    if (!coveredLocations.has(uid)) names.push(pathLabels.get(uid) ?? uid)
  }
  return names.sort((a, b) => a.localeCompare(b))
}

/** Per-cadence tuning defaults (backend access/models.py CADENCE_DEFAULTS);
 *  shown as the tuning fields' placeholder text. */
const CADENCE_DEFAULTS: Record<DoorCodeSeriesCadence, { overlap_days: number; ahead: number; keep_expired: number }> = {
  monthly: { overlap_days: 2, ahead: 2, keep_expired: 1 },
  quarterly: { overlap_days: 3, ahead: 2, keep_expired: 1 },
  semiannual: { overlap_days: 7, ahead: 1, keep_expired: 1 },
}

const CADENCE_OPTIONS: { value: DoorCodeSeriesCadence; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semiannual' },
]

/** Random digits-only code (crypto-backed), `length` capped 4..10. */
function generateCode(length: number): string {
  const digits = new Uint32Array(length)
  crypto.getRandomValues(digits)
  return Array.from(digits, (d) => String(d % 10)).join('')
}

/** Static-code create/edit modal. Edit is a full-replace PUT: the door
 *  scope prefills from the code's current doors (the one-shot location
 *  resolution is not recoverable — newly picked locations union on top). */
function DoorCodeFormModal({
  open,
  code,
  onClose,
}: {
  open: boolean
  /** When set, edits this code; otherwise creates a new one. */
  code: DoorCode | null
  onClose: () => void
}) {
  const create = useCreateDoorCode()
  const update = useUpdateDoorCode()
  const schedules = useSchedules('door')

  const [digits, setDigits] = useState('')
  const [label, setLabel] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const [schedule, setSchedule] = useState('')
  const [locations, setLocations] = useState<string[]>([])
  const [doors, setDoors] = useState<string[]>([])
  const [status, setStatus] = useState('active')

  useEffect(() => {
    if (!open) return
    // A masked code ('••••' for non-admins) must never round-trip into a PUT.
    setDigits(code && !code.code.includes('•') ? code.code : '')
    setLabel(code?.label ?? '')
    setValidFrom(fmtDate(code?.valid_from ?? null) ?? '')
    setValidTo(fmtDate(code?.valid_to ?? null) ?? '')
    setSchedule(code?.schedule ?? '')
    setLocations([])
    setDoors(code?.doors ?? [])
    setStatus(code?.status ?? 'active')
    // Only (re)initialize when the modal opens or the target code changes.
  }, [open, code])

  const save = code ? update : create
  const codeMasked = code !== null && code.code.includes('•')
  const valid = digits.length > 0 && /^\d+$/.test(digits) && (locations.length > 0 || doors.length > 0)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!valid || save.isPending) return
    const body = {
      code: digits,
      label: label.trim(),
      doors,
      locations,
      schedule: schedule === '' ? null : schedule,
      valid_from: validFrom === '' ? null : validFrom,
      valid_to: validTo === '' ? null : validTo,
      status,
    }
    if (code) update.mutate({ uid: code.unique_id, body }, { onSuccess: onClose })
    else create.mutate(body, { onSuccess: onClose })
  }

  return (
    <Modal open={open} title={code ? 'Edit door code' : 'New door code'} onClose={onClose}>
      <form onSubmit={submit} className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto pr-1">
        <div className="flex items-end gap-2">
          <Input
            label="Code"
            value={digits}
            onChange={(e) => setDigits(e.target.value.replace(/\D/g, ''))}
            placeholder="4–10 digits"
            disabled={codeMasked}
            autoFocus
            className="tnum font-mono"
          />
          <Button type="button" variant="ghost" size="sm" onClick={() => setDigits(generateCode(4))}>
            Generate
          </Button>
        </div>
        {codeMasked && (
          <p className="text-[11px] text-ostrel-ink-muted">
            The code is masked for your role — set a new one only if you mean to replace it.
          </p>
        )}
        <Input
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Cleaner front door"
        />
        <div className="grid gap-2 md:grid-cols-2">
          <Input
            label="Valid from (optional)"
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
          <Input
            label="Valid to — max date (optional)"
            type="date"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
          />
        </div>
        <SelectField
          label="Door schedule (optional)"
          value={schedule}
          onChange={setSchedule}
          options={[
            { value: '', label: '— none' },
            ...(schedules.data ?? []).map((s) => ({ value: s.unique_id, label: s.name })),
          ]}
        />
        <ScopePicker locations={locations} doors={doors} onLocations={setLocations} onDoors={setDoors} />
        {code && (
          <SelectField
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'disabled', label: 'Disabled' },
            ]}
          />
        )}
        {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="navy" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Saving…' : code ? 'Save changes' : 'Create code'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/** Series create/edit modal. Tuning fields are optional — empty follows the
 *  cadence default (placeholder text, recomputed when cadence changes); on
 *  edit they prefill the RESOLVED values so a save never silently re-pins
 *  or resets them (clearing one sends null = back to the cadence default). */
function SeriesFormModal({
  open,
  series,
  onClose,
}: {
  open: boolean
  /** When set, edits this series; otherwise creates a new one. */
  series: DoorCodeSeries | null
  onClose: () => void
}) {
  const create = useCreateDoorCodeSeries()
  const update = useUpdateDoorCodeSeries()
  const schedules = useSchedules('door')

  const [name, setName] = useState('')
  const [cadence, setCadence] = useState<DoorCodeSeriesCadence>('monthly')
  const [locations, setLocations] = useState<string[]>([])
  const [doors, setDoors] = useState<string[]>([])
  const [schedule, setSchedule] = useState('')
  const [codeLength, setCodeLength] = useState('4')
  const [overlapDays, setOverlapDays] = useState('')
  const [ahead, setAhead] = useState('')
  const [keepExpired, setKeepExpired] = useState('')

  useEffect(() => {
    if (!open) return
    setName(series?.name ?? '')
    setCadence(series?.cadence ?? 'monthly')
    setLocations(series?.locations ?? [])
    setDoors(series?.doors ?? [])
    setSchedule(series?.schedule ?? '')
    setCodeLength(String(series?.code_length ?? 4))
    setOverlapDays(series ? String(series.overlap_days) : '')
    setAhead(series ? String(series.ahead) : '')
    setKeepExpired(series ? String(series.keep_expired) : '')
    // Only (re)initialize when the modal opens or the target series changes.
  }, [open, series])

  const save = series ? update : create
  const length = Number.parseInt(codeLength, 10)
  const tuningValid =
    [overlapDays, ahead, keepExpired].every((v) => v === '' || /^\d+$/.test(v)) &&
    Number.isInteger(length) &&
    length >= 4 &&
    length <= 10
  const valid = name.trim() !== '' && (locations.length > 0 || doors.length > 0) && tuningValid

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!valid || save.isPending) return
    const tuning = {
      overlap_days: overlapDays === '' ? null : Number.parseInt(overlapDays, 10),
      ahead: ahead === '' ? null : Number.parseInt(ahead, 10),
      keep_expired: keepExpired === '' ? null : Number.parseInt(keepExpired, 10),
    }
    if (series) {
      update.mutate(
        {
          uid: series.unique_id,
          body: {
            name: name.trim(),
            cadence,
            locations,
            doors,
            schedule: schedule === '' ? null : schedule,
            code_length: length,
            ...tuning,
          },
        },
        { onSuccess: onClose },
      )
    } else {
      create.mutate(
        {
          name: name.trim(),
          cadence,
          locations,
          doors,
          schedule: schedule === '' ? null : schedule,
          code_length: length,
          ...tuning,
        },
        { onSuccess: onClose },
      )
    }
  }

  const defaults = CADENCE_DEFAULTS[cadence]

  return (
    <Modal open={open} title={series ? 'Edit series' : 'New series'} onClose={onClose}>
      <form onSubmit={submit} className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto pr-1">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Stairwell keypads"
          autoFocus
        />
        <SelectField
          label="Cadence"
          value={cadence}
          onChange={(v) => setCadence(v as DoorCodeSeriesCadence)}
          options={CADENCE_OPTIONS}
        />
        <ScopePicker locations={locations} doors={doors} onLocations={setLocations} onDoors={setDoors} />
        <div className="grid gap-2 md:grid-cols-2">
          <SelectField
            label="Door schedule (optional)"
            value={schedule}
            onChange={setSchedule}
            options={[
              { value: '', label: '— none' },
              ...(schedules.data ?? []).map((s) => ({ value: s.unique_id, label: s.name })),
            ]}
          />
          <Input
            label="Code length"
            inputMode="numeric"
            value={codeLength}
            onChange={(e) => setCodeLength(e.target.value.replace(/\D/g, ''))}
            placeholder="4"
          />
        </div>
        <div className="grid gap-2 md:grid-cols-3">
          <Input
            label="Overlap days (optional)"
            inputMode="numeric"
            value={overlapDays}
            onChange={(e) => setOverlapDays(e.target.value.replace(/\D/g, ''))}
            placeholder={`default: ${defaults.overlap_days} days`}
          />
          <Input
            label="Periods ahead (optional)"
            inputMode="numeric"
            value={ahead}
            onChange={(e) => setAhead(e.target.value.replace(/\D/g, ''))}
            placeholder={`default: ${defaults.ahead}`}
          />
          <Input
            label="Keep expired (optional)"
            inputMode="numeric"
            value={keepExpired}
            onChange={(e) => setKeepExpired(e.target.value.replace(/\D/g, ''))}
            placeholder={`default: ${defaults.keep_expired}`}
          />
        </div>
        {!tuningValid && (
          <p className="text-xs text-ostrel-danger">Code length must be 4–10; tuning fields are whole numbers.</p>
        )}
        {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="navy" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Saving…' : series ? 'Save changes' : 'Create series'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/** The expanded series' period codes (GET /door-codes?series=<uid>), each
 *  with a copy-for-newsletter button (JM's copyQuarterSummary shape): a
 *  `{period} ({from} - {display end})` header then one `{door}: {code}`
 *  line per scoped door, sorted by name. Admin-only: a masked code
 *  ('••••' for non-admins) hides the button. */
function SeriesCodes({ series }: { series: DoorCodeSeries }) {
  const codes = useDoorCodes(series.unique_id)
  const doors = useDoors()
  const locationTree = useLocationTree()
  const pathLabels = useMemo(() => locationPaths(locationTree.data ?? []), [locationTree.data])
  const scopeLines = useMemo(
    () => seriesScopeLines(series, doors.data ?? [], locationTree.data ?? [], pathLabels),
    [series, doors.data, locationTree.data, pathLabels],
  )
  const [copied, setCopied] = useState<string | null>(null)

  const copyPeriod = (c: DoorCode) => {
    const header = `${c.period || c.label} (${fmtDate(c.valid_from) ?? 'open'} - ${displayEndDate(c.valid_to, series.overlap_days)})`
    const text = [header, ...scopeLines.map((name) => `${name}: ${c.code}`)].join('\n')
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(c.period || c.label)
      setTimeout(() => setCopied((cur) => (cur === (c.period || c.label) ? null : cur)), 3000)
    })
  }

  if (codes.isPending) return <Skeleton className="h-8" />
  if (codes.isError) return <p className="text-[11px] text-ostrel-danger">{errText(codes.error)}</p>
  if (codes.data.length === 0)
    return <p className="text-[11px] text-ostrel-ink-muted">No period codes yet — the first roll creates them.</p>
  return (
    <div className="flex flex-col gap-1">
      {copied && (
        <p className="tnum rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3 py-1.5 text-[11px] text-ostrel-ink">
          Copied {copied} summary
        </p>
      )}
      <ul className="flex flex-col gap-1">
        {codes.data.map((c) => {
          const status = doorCodeStatus(c)
          const window = [fmtDate(c.valid_from), fmtDate(c.valid_to)].filter(Boolean).join(' → ')
          const masked = c.code.includes('•')
          return (
            <li key={c.unique_id} className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="tnum font-mono text-ostrel-ink">{c.code}</span>
              <span className="min-w-0 truncate text-ostrel-ink">{c.label || c.period || c.unique_id}</span>
              {c.period && <Chip tone="muted" label={c.period} />}
              {window && <span className="tnum text-ostrel-ink-muted">{window}</span>}
              <span className="ml-auto flex items-center gap-2">
                {!masked && (
                  <Button variant="ghost" size="sm" onClick={() => copyPeriod(c)}>
                    Copy
                  </Button>
                )}
                <Chip tone={status.tone} label={status.label} />
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** One series row: summary + actions (Roll now with its transient result
 *  line, pause/resume, edit, delete) + expandable period codes. */
function SeriesRow({
  series,
  onEdit,
  onDelete,
}: {
  series: DoorCodeSeries
  onEdit: () => void
  onDelete: () => void
}) {
  const roll = useRollDoorCodeSeries()
  const update = useUpdateDoorCodeSeries()
  const [expanded, setExpanded] = useState(false)
  const [rollResult, setRollResult] = useState<RollSummary | null>(null)

  const scopeSummary = [
    series.locations.length > 0 ? `${series.locations.length} location${series.locations.length === 1 ? '' : 's'}` : null,
    series.doors.length > 0 ? `${series.doors.length} door${series.doors.length === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' + ')

  const period = series.current_period
  const paused = series.status === 'paused'

  return (
    <li className="flex flex-col gap-2 rounded-sm border border-ostrel-line bg-ostrel-card px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 truncate text-sm font-medium text-ostrel-ink">{series.name}</span>
        <Chip tone="info" label={series.cadence} />
        {paused && <Chip tone="muted" label="paused" />}
        <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">{scopeSummary}</span>
      </div>
      <p className="tnum text-[11px] text-ostrel-ink-muted">
        {period
          ? `${period.label} · ${fmtDate(period.valid_from)} → ${fmtDate(period.valid_to)} · ${period.codes} code${period.codes === 1 ? '' : 's'}`
          : 'No current period'}
      </p>
      {series.last_error !== '' && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {series.last_error}
        </p>
      )}
      {rollResult && (
        <p className="tnum flex items-center gap-2 rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3 py-1.5 text-[11px] text-ostrel-ink">
          <span className="min-w-0 flex-1 truncate">{rollSummaryLine(rollResult)}</span>
          <button
            type="button"
            aria-label="Dismiss roll result"
            onClick={() => setRollResult(null)}
            className="text-ostrel-ink-muted transition-colors hover:text-ostrel-ink focus:outline-none"
          >
            ✕
          </button>
        </p>
      )}
      {rollResult && rollResult.errors.length > 0 && (
        <ul className="flex flex-col gap-0.5">
          {rollResult.errors.map((err, i) => (
            <li key={i} className="text-[11px] text-ostrel-danger">
              {err}
            </li>
          ))}
        </ul>
      )}
      {roll.isError && <p className="text-[11px] text-ostrel-danger">{errText(roll.error)}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="navy"
          size="sm"
          disabled={roll.isPending}
          onClick={() =>
            roll.mutate(series.unique_id, {
              onSuccess: (summary) => setRollResult(summary),
            })
          }
        >
          {roll.isPending ? 'Rolling…' : 'Roll now'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={update.isPending}
          onClick={() =>
            update.mutate({ uid: series.unique_id, body: { status: paused ? 'active' : 'paused' } })
          }
        >
          {paused ? 'Resume' : 'Pause'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
        <Button variant="danger" size="sm" onClick={onDelete}>
          Delete
        </Button>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="ml-auto text-[11px] text-ostrel-ink-muted transition-colors hover:text-ostrel-ink focus:outline-none"
        >
          {expanded ? 'Hide codes ▴' : 'Show codes ▾'}
        </button>
      </div>
      {expanded && (
        <div className="border-t border-ostrel-line pt-2">
          <SeriesCodes series={series} />
        </div>
      )}
    </li>
  )
}

export default function DoorCodesPage() {
  const [params, setParams] = useSearchParams()
  const locationUid = params.get('location') ?? undefined

  const locationTree = useLocationTree()
  const locationName = useMemo(() => {
    if (!locationUid || !locationTree.data) return undefined
    return locationPaths(locationTree.data).get(locationUid) ?? locationUid
  }, [locationUid, locationTree.data])

  const staticCodes = useDoorCodes('none', locationUid)
  const series = useDoorCodeSeries()
  const systems = useSystems()
  const providerCaps = useProviderCapabilities()
  const doors = useDoors()

  const [codeFormOpen, setCodeFormOpen] = useState(false)
  const [editingCode, setEditingCode] = useState<DoorCode | null>(null)
  const [seriesFormOpen, setSeriesFormOpen] = useState(false)
  const [editingSeries, setEditingSeries] = useState<DoorCodeSeries | null>(null)
  const [confirmDeleteSeries, setConfirmDeleteSeries] = useState<DoorCodeSeries | null>(null)
  const deleteSeries = useDeleteDoorCodeSeries()
  /** Static-row copy confirmation: the copied row's uid, briefly. */
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  /** `{label or code}: {code}` — JM's one-liner per static code. */
  const copyStaticCode = (c: DoorCode) => {
    void navigator.clipboard?.writeText(`${c.label || c.code}: ${c.code}`).then(() => {
      setCopiedCode(c.unique_id)
      setTimeout(() => setCopiedCode((cur) => (cur === c.unique_id ? null : cur)), 3000)
    })
  }

  /** Rotation is only interesting when a connected system's provider
   *  participates (came='windows' today); otherwise the Dynamic section
   *  gets an info callout instead of an empty list. */
  const anyRotation = (systems.data ?? []).some(
    (s) => (providerCaps.data?.[s.provider]?.door_code_rotation ?? 'none') !== 'none',
  )

  const doorById = useMemo(() => new Map((doors.data ?? []).map((d) => [d.unique_id, d])), [doors.data])
  const locationLabels = useMemo(() => locationPaths(locationTree.data ?? []), [locationTree.data])

  const openCreateCode = () => {
    setEditingCode(null)
    setCodeFormOpen(true)
  }
  const openEditCode = (c: DoorCode) => {
    setEditingCode(c)
    setCodeFormOpen(true)
  }
  const openCreateSeries = () => {
    setEditingSeries(null)
    setSeriesFormOpen(true)
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Access"
        title="Door codes"
        action={<Button onClick={openCreateCode}>+ New code</Button>}
      />

      {locationName && (
        <div className="flex items-center gap-2 text-[11px] text-ostrel-ink-muted">
          <span>Showing codes at</span>
          <Chip tone="info" label={locationName} />
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(params)
              next.delete('location')
              setParams(next, { replace: true })
            }}
            className="text-ostrel-gold hover:text-ostrel-accent"
          >
            Clear
          </button>
        </div>
      )}

      <Card title="Static codes">
        {staticCodes.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : staticCodes.isError ? (
          <EmptyState title="Could not load door codes" hint={errText(staticCodes.error)} />
        ) : staticCodes.data.length === 0 ? (
          <EmptyState
            title={locationName ? `No static codes at “${locationName}”` : 'No static codes'}
            hint={
              locationName
                ? 'No static door codes are attached to doors at this location or below it.'
                : 'Static codes keep their digits until you change them — rotating codes live in a series below.'
            }
            action={
              !locationName ? (
                <Button size="sm" onClick={openCreateCode}>
                  + New code
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Code</TH>
                <TH>Label</TH>
                <TH>Window</TH>
                <TH>Doors</TH>
                <TH>Status</TH>
                <TH className="text-right">Action</TH>
              </tr>
            </THead>
            <TBody>
              {staticCodes.data.map((c) => {
                const status = doorCodeStatus(c)
                const window = [fmtDate(c.valid_from), fmtDate(c.valid_to)].filter(Boolean).join(' → ')
                // A code bound to a dry-run system is read-only.
                const hint = readOnlyReason(c)
                return (
                  <TR key={c.unique_id}>
                    <TD>
                      <span className="tnum font-mono text-sm text-ostrel-ink">{c.code}</span>
                    </TD>
                    <TD>
                      <span className="block text-sm text-ostrel-ink">{c.label || '—'}</span>
                      <span className="tnum block font-mono text-[10px] text-ostrel-ink-muted">
                        {c.unique_id}
                      </span>
                    </TD>
                    <TD>
                      <span className="tnum text-xs text-ostrel-ink-muted">{window || 'open'}</span>
                    </TD>
                    <TD>
                      <span className="block max-w-64 truncate text-xs text-ostrel-ink-muted">
                        {c.doors
                          .map((uid) => doorById.get(uid))
                          .filter((d) => d !== undefined)
                          .map((d) => `${d.name} (${locationLabels.get(d.location) ?? d.location})`)
                          .join(' · ') || '—'}
                      </span>
                    </TD>
                    <TD>
                      <Chip tone={status.tone} label={status.label} />
                    </TD>
                    <TD className="text-right">
                      <span className="flex items-center justify-end gap-2">
                        {!c.code.includes('•') && (
                          <Button variant="ghost" size="sm" onClick={() => copyStaticCode(c)}>
                            {copiedCode === c.unique_id ? 'Copied' : 'Copy'}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={hint !== undefined}
                          title={hint}
                          onClick={() => openEditCode(c)}
                        >
                          Edit
                        </Button>
                      </span>
                    </TD>
                  </TR>
                )
              })}
            </TBody>
          </Table>
        )}
      </Card>

      <Card
        title="Dynamic codes"
        action={
          <Button variant="ghost" size="sm" onClick={openCreateSeries}>
            + New series
          </Button>
        }
      >
        {!anyRotation && (
          <p className="mb-3 rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3 py-2 text-[11px] text-ostrel-ink-muted">
            No connected system supports rotating door codes yet — series still generate canonical codes
            here, but nothing pushes them to a panel.
          </p>
        )}
        {series.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        ) : series.isError ? (
          <EmptyState title="Could not load series" hint={errText(series.error)} />
        ) : series.data.length === 0 ? (
          <EmptyState
            title="No series yet"
            hint="A series generates a fresh code per period (monthly, quarterly, semiannual) over its scope and retires the old ones."
            action={
              <Button size="sm" onClick={openCreateSeries}>
                + New series
              </Button>
            }
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {series.data.map((s) => (
              <SeriesRow
                key={s.unique_id}
                series={s}
                onEdit={() => {
                  setEditingSeries(s)
                  setSeriesFormOpen(true)
                }}
                onDelete={() => setConfirmDeleteSeries(s)}
              />
            ))}
          </ul>
        )}
      </Card>

      <DoorCodeFormModal open={codeFormOpen} code={editingCode} onClose={() => setCodeFormOpen(false)} />
      <SeriesFormModal open={seriesFormOpen} series={editingSeries} onClose={() => setSeriesFormOpen(false)} />

      <ConfirmDeleteModal
        open={confirmDeleteSeries !== null}
        title="Delete series"
        objectName={confirmDeleteSeries?.name ?? ''}
        expected={confirmDeleteSeries?.unique_id ?? ''}
        pending={deleteSeries.isPending}
        error={deleteSeries.isError ? errText(deleteSeries.error) : null}
        confirmLabel={deleteSeries.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() =>
          confirmDeleteSeries &&
          deleteSeries.mutate(confirmDeleteSeries.unique_id, { onSuccess: () => setConfirmDeleteSeries(null) })
        }
        onClose={() => setConfirmDeleteSeries(null)}
      >
        <p>
          Delete <span className="font-medium">{confirmDeleteSeries?.name}</span>? Surviving period codes
          become ordinary static codes — they are never deleted with the series.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}
